import type { ReactNode } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
} from "@heroicons/react/solid";
import { TicketIcon } from "@heroicons/react/outline";
import Button from "components/Button/Button";
import EventCoverPlayer from "components/EventCoverPlayer/EventCoverPlayer";
import Loader from "components/Loader/Loader";
import type { EventItem } from "types/user";
import type { EventTicketSelectionModalProps } from "./EventTicketSelectionModal";
import { findEventLevelDiscount } from "../eventPricing";
import EventPromotionAction from "./EventPromotionAction";

const mobilePanelClassName = "lg:hidden mb-4";
const desktopPanelClassName = "col-span-4 order-first hidden lg:block";
const mobileTicketCardClassName = "bg-slate-50 rounded-xl";
const desktopTicketCardClassName =
  "lg:bg-slate-50 p-4 lg:shadow lg:rounded-xl mb-4";
const ticketListTitleClassName = "mb-5 text-base text-center";
const ticketRowBaseClassName =
  "m-auto flex items-start justify-between gap-3 rounded-xl mb-4 p-4";
const ticketRowAvailableClassName = "border border-slate-500";
const ticketRowMobileClassName = "md:w-11/12";
const ticketRowDesktopClassName = "w-11/12 shadow";
const ticketStatusIndicatorClassName = "h-3 w-3 rounded-full border";
const buyButtonContainerClassName = "inline-flex w-3/4 justify-center";

// eslint-disable-next-line no-unused-vars
type Translate = (key: string) => string;

interface TicketPurchasePanelProps extends EventTicketSelectionModalProps {
  variant: "mobile" | "desktop";
  isSoldEnabled: boolean;
  isMyProfile: boolean;
  handleOpenPromoModal: () => void;
  translate: Translate;
  cloudfrontUrl: string;
}

export const TicketPurchasePanel = ({
  purchase,
  ...props
}: TicketPurchasePanelProps) => {
  const isMobile = props.variant === "mobile";
  const shared = {
    ...props,
    event: props.event,
    promotionCode: props.promotionCode,
    countryCurrency: props.countryCurrency,
    isMobile,
    isLevelSoldOut: purchase.availability.isLevelSoldOut,
    isSoldOut: purchase.availability.isSoldOut,
    seatsLeftAllLevels: () => purchase.availability.label,
    handlePlanChange: purchase.preview.handlePlanChange,
  };
  return (
    <TicketPurchasePanelFrame
      {...props}
      isLoading={purchase.availability.isLoading}
    >
      {purchase.kind === "seats" ? (
        <>
          <SeatTicketPlans
            {...shared}
            assignedSeatingTicketTypes={purchase.selection.ticketTypes}
            handleOpenSeatPicker={purchase.start}
          />
          <SeatPurchaseActions
            {...shared}
            handleOpenSeatPicker={purchase.start}
          />
        </>
      ) : (
        <>
          <QuantityTicketPlans {...shared} {...purchase.selection} />
          <QuantityPurchaseActions
            {...shared}
            {...purchase.selection}
            totalSum={purchase.checkout.total}
            handleBuy={purchase.start}
          />
        </>
      )}
    </TicketPurchasePanelFrame>
  );
};

interface TicketPurchasePanelFrameProps {
  variant: "mobile" | "desktop";
  isLoading: boolean;
  event?: EventItem;
  translate: Translate;
  cloudfrontUrl: string;
  children: ReactNode;
}

const TicketPurchasePanelFrame = ({
  variant,
  isLoading,
  event,
  translate,
  cloudfrontUrl,
  children,
}: TicketPurchasePanelFrameProps) => {
  const isMobile = variant === "mobile";
  return (
    <div className={isMobile ? mobilePanelClassName : desktopPanelClassName}>
      <div
        className={
          isMobile ? mobileTicketCardClassName : desktopTicketCardClassName
        }
      >
        {!isMobile && (
          <EventCoverPlayer
            videoUrl={
              event?.cover !== null ? cloudfrontUrl + event?.cover : null
            }
            adaptivePlaylistCover={event?.adaptivePlaylistCover}
          />
        )}
        <h1 className={`${ticketListTitleClassName}${isMobile ? "" : " pt-6"}`}>
          {translate("selectTickets")}
        </h1>
        {isLoading ? (
          <div className="pb-6" role="status" aria-busy="true">
            <Loader fullScreen={false} />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

const QuantityTicketPlans = (props) =>
  props.event?.levels.map((plan, index) => (
    <TicketPlanRow
      key={plan.name}
      isMobile={props.isMobile}
      plan={plan}
      discount={levelDiscount(props.promotionCode, plan)}
      isPlanSoldOut={props.isLevelSoldOut(plan)}
      countryCurrency={props.countryCurrency}
      translate={props.translate}
      onSelect={
        props.handlePlanChange ? () => props.handlePlanChange(index) : undefined
      }
      leadingIndicator={
        <EventTicketAvailabilityIndicator
          color={plan.color}
          isSoldOut={props.isLevelSoldOut(plan)}
        />
      }
      trailingContent={
        <TicketQuantityControls
          index={index}
          isMobile={props.isMobile}
          isPlanSoldOut={props.isLevelSoldOut(plan)}
          count={props.countEvent[index]}
          onDecrement={props.decrementCount}
          onIncrement={props.incrementCount}
        />
      }
    />
  ));

const SeatTicketPlans = (props) =>
  props.event?.levels.map((plan) => (
    <AssignedTicketPlanRow
      key={plan.name}
      isMobile={props.isMobile}
      plan={plan}
      ticketType={props.assignedSeatingTicketTypes.find(
        (type) =>
          type.key === plan.seatingTicketTypeKey ||
          type.key === plan.levelid ||
          type.name === plan.name
      )}
      discount={levelDiscount(props.promotionCode, plan)}
      isPlanSoldOut={props.isLevelSoldOut(plan)}
      countryCurrency={props.countryCurrency}
      onSelect={props.handleOpenSeatPicker}
      translate={props.translate}
    />
  ));

// Obsolete UI factories. Kept only for legacy panel callers, not purchase models.
interface LegacyTicketPurchasePanelModel {
  isLoading: boolean;
  // eslint-disable-next-line no-unused-vars
  renderPlans: (isMobile: boolean) => ReactNode;
  // eslint-disable-next-line no-unused-vars
  renderActions: (isMobile: boolean) => ReactNode;
}

export const createQuantityPurchasePanel = (
  props
): LegacyTicketPurchasePanelModel => ({
  isLoading: false,
  renderPlans: (isMobile) => QuantityTicketPlans({ ...props, isMobile }),
  renderActions: (isMobile) => (
    <QuantityPurchaseActions {...props} isMobile={isMobile} />
  ),
});

export const createSeatPurchasePanel = (
  props
): LegacyTicketPurchasePanelModel => ({
  isLoading: props.isSeatMapLoading,
  renderPlans: (isMobile) => SeatTicketPlans({ ...props, isMobile }),
  renderActions: (isMobile) => (
    <SeatPurchaseActions {...props} isMobile={isMobile} />
  ),
});

// Obsolete prop adapter. Existing callers remain compatible; the app uses TicketPurchasePanel.
const EventTicketPurchasePanel = (props: any) => {
  const model = props.isAssignedSeatingEvent
    ? createSeatPurchasePanel(props)
    : createQuantityPurchasePanel(props);
  const isMobile = props.variant === "mobile";
  return (
    <TicketPurchasePanelFrame {...props} isLoading={props.isSeatMapLoading}>
      {!props.isSeatMapLoading && (
        <>
          {model.renderPlans(isMobile)}
          {model.renderActions(isMobile)}
        </>
      )}
    </TicketPurchasePanelFrame>
  );
};

export const EventTicketAvailabilityIndicator = ({
  color,
  isSoldOut,
}: {
  color?: string;
  isSoldOut: boolean;
}) => (
  <span
    aria-hidden="true"
    className={`mr-3 mt-1 h-4 w-4 shrink-0 rounded-full border ${
      isSoldOut
        ? "border-gray-300 bg-transparent"
        : "border-transparent bg-green-400"
    }`}
    style={!isSoldOut && color ? { backgroundColor: color } : undefined}
  />
);

export const AssignedSeatingAvailabilityIcon = () => (
  <img
    src="/img/icons/seating-chart.svg"
    alt=""
    aria-hidden="true"
    className="mr-1 h-5 w-5"
  />
);

export const AssignedSeatingTicketPlanList = ({
  isMobile,
  event,
  promotionCode,
  countryCurrency,
  assignedSeatingTicketTypes,
  onOpen,
  translate,
}) => (
  <div className="w-full" data-testid="assigned-seating-ticket-level-list">
    <h1 className={`${ticketListTitleClassName}${isMobile ? "" : " pt-6"}`}>
      {translate("selectTickets")}
    </h1>
    {assignedSeatingTicketTypes.map((ticketType) => {
      const level = event?.levels.find(
        (candidate) => candidate.seatingTicketTypeKey === ticketType.key
      );
      return (
        <AssignedTicketPlanRow
          key={ticketType.key}
          isMobile={isMobile}
          plan={{ ...level, name: ticketType.name }}
          ticketType={ticketType}
          discount={level ? levelDiscount(promotionCode, level) : null}
          isPlanSoldOut={ticketType.availableSeats <= 0}
          countryCurrency={countryCurrency}
          onSelect={onOpen}
          translate={translate}
        />
      );
    })}
  </div>
);

const TicketPlanRow = ({
  asButton = false,
  isMobile,
  plan,
  discount,
  isPlanSoldOut,
  countryCurrency,
  onSelect,
  leadingIndicator,
  nameIndicator,
  trailingContent,
  translate,
}: {
  asButton?: boolean;
  isMobile: boolean;
  plan: { name: string; price?: number | string };
  discount?: number | string;
  isPlanSoldOut: boolean;
  countryCurrency?: { symbol?: string };
  onSelect?: () => void;
  leadingIndicator?: ReactNode;
  nameIndicator?: ReactNode;
  trailingContent?: ReactNode;
  translate: Translate;
}) => {
  const Row = asButton ? "button" : "div";
  return (
    <Row
      type={asButton ? "button" : undefined}
      disabled={asButton ? isPlanSoldOut : undefined}
      className={ticketRowClassName({
        asButton,
        isMobile,
        isPlanSoldOut,
        isInteractive: Boolean(onSelect),
      })}
      onClick={onSelect}
    >
      <div className="flex min-w-0 flex-1">
        {leadingIndicator}
        <div className="min-w-0 flex-1 text-base">
          <div className="flex items-center gap-2 text-grey-900 font-medium">
            {nameIndicator}
            <span className="min-w-0 break-words">{plan.name}</span>
          </div>
          <div>
            <span
              className={`${
                isPlanSoldOut ? "text-gray-300" : "text-gray-900"
              } font-medium`}
            >
              {isPlanSoldOut
                ? translate("soldOut")
                : countryCurrency?.symbol + plan.price}
            </span>
            {Number(discount) > 0 ? (
              <span className="text-slate-500"> (-{discount}%)</span>
            ) : null}
          </div>
        </div>
      </div>
      {trailingContent}
    </Row>
  );
};

const AssignedTicketPlanRow = ({
  isMobile,
  plan,
  ticketType,
  discount,
  isPlanSoldOut,
  countryCurrency,
  onSelect,
  translate,
}: any) => {
  const color = ticketType?.color || plan.color;
  return (
    <TicketPlanRow
      asButton
      isMobile={isMobile}
      plan={{ name: ticketType?.name || plan.name, price: ticketType?.price }}
      discount={discount}
      isPlanSoldOut={isPlanSoldOut}
      countryCurrency={countryCurrency}
      translate={translate}
      onSelect={isPlanSoldOut ? undefined : onSelect}
      nameIndicator={
        <span
          aria-hidden="true"
          className={`${ticketStatusIndicatorClassName} shrink-0 ${
            isPlanSoldOut ? "border-gray-300" : "border-transparent"
          }`}
          style={
            !isPlanSoldOut && color ? { backgroundColor: color } : undefined
          }
        />
      }
      trailingContent={
        ticketType ? (
          <span className="shrink-0 text-sm text-slate-500">
            {ticketType.availableSeats} {translate("seats")}
          </span>
        ) : null
      }
    />
  );
};

const TicketQuantityControls = ({
  index,
  isMobile,
  isPlanSoldOut,
  count,
  onDecrement,
  onIncrement,
}) => {
  const disabledClassName = isPlanSoldOut ? "opacity-50" : "";
  return (
    <div className="flex shrink-0 justify-center">
      <button
        onClick={() => onDecrement(index)}
        className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-l mr-2 ${disabledClassName}`}
      >
        <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
      </button>
      <span className="flex items-center mr-2">{count}</span>
      <button
        data-testid={`select-count-tickets${
          isMobile ? "-mobile" : ""
        }[${index}]`}
        onClick={() => onIncrement(index)}
        disabled={isPlanSoldOut}
        className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-r ${disabledClassName}`}
      >
        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
      </button>
    </div>
  );
};

const SoldOutTicketsButton = ({ translate }) => (
  <Button
    className="w-3/4"
    icon={<TicketIcon className="h-6 w-6" />}
    type="sold"
    disabled={true}
    fluid
  >
    {translate("soldOut")}
  </Button>
);

const SeatPurchaseActions = ({
  isMobile,
  event,
  promotionCode,
  isMyProfile,
  handleOpenPromoModal,
  isSoldOut,
  seatsLeftAllLevels,
  handleOpenSeatPicker,
  translate,
}) => (
  <div className="flex flex-col items-center justify-center">
    {isSoldOut(event?.levels) ? (
      <SoldOutTicketsButton translate={translate} />
    ) : (
      <>
        <div
          className={buyButtonContainerClassName}
          data-testid={
            isMobile ? "assigned-seating-buy-mobile" : "assigned-seating-buy"
          }
        >
          <Button
            className="w-3/4"
            icon={<TicketIcon className="h-6 w-6" />}
            type="danger"
            onClick={handleOpenSeatPicker}
            fluid
          >
            {translate("buy")}
          </Button>
        </div>
        {!isMyProfile && !promotionCode ? (
          <EventPromotionAction
            label={translate("enterPromoCodePromotions")}
            onClick={handleOpenPromoModal}
          />
        ) : null}
      </>
    )}
    <div className="mt-4 mb-2 flex items-center justify-center">
      <AssignedSeatingAvailabilityIcon />
      <span className="text-sm">{seatsLeftAllLevels(event?.levels)}</span>
    </div>
  </div>
);

const QuantityPurchaseActions = ({
  isMobile,
  event,
  promotionCode,
  countryCurrency,
  countEvent,
  totalSum,
  isSoldEnabled,
  isMyProfile,
  isSoldOut,
  seatsLeftAllLevels,
  handleBuy,
  handleOpenPromoModal,
  translate,
}) => (
  <div className="flex flex-col items-center justify-center">
    {isSoldOut(event?.levels) ? (
      <SoldOutTicketsButton translate={translate} />
    ) : totalSum === 0 && countEvent.every((count) => count === 0) ? (
      <Button
        className="w-3/4 text-sm"
        icon={<TicketIcon className="h-6 w-6" />}
        type="noTickets"
        disabled={true}
        fluid
      >
        {translate("noTicketsSelected")}
      </Button>
    ) : (
      <>
        <div
          className={buyButtonContainerClassName}
          data-testid={isMobile ? "buy-button-mobile" : "buy-button"}
        >
          <Button
            className="w-3/4"
            icon={<TicketIcon className="h-6 w-6" />}
            type="danger"
            onClick={handleBuy}
            fluid
          >
            {totalSum === 0
              ? translate("attendForfree")
              : `${translate("buyFor")} ${countryCurrency?.symbol}${totalSum}`}
          </Button>
        </div>
        {!isMyProfile && !promotionCode ? (
          <EventPromotionAction
            label={translate("enterPromoCodePromotions")}
            onClick={handleOpenPromoModal}
          />
        ) : null}
      </>
    )}
    {isSoldEnabled && (
      <div className="flex justify-center items-center mt-4 mb-2">
        <UserGroupIcon className="w-5 h-5 mr-1" />
        <span className="text-sm">{seatsLeftAllLevels(event?.levels)}</span>
      </div>
    )}
  </div>
);

const levelDiscount = (promotion, plan) =>
  promotion?.levels_discount
    ? findEventLevelDiscount(promotion.levels_discount, plan.levelid)?.discount
    : undefined;

const ticketRowClassName = ({
  asButton,
  isMobile,
  isPlanSoldOut,
  isInteractive,
}) =>
  [
    ticketRowBaseClassName,
    isMobile ? ticketRowMobileClassName : ticketRowDesktopClassName,
    isPlanSoldOut ? "" : ticketRowAvailableClassName,
    isInteractive ? "cursor-pointer" : "",
    asButton ? "text-left disabled:cursor-default" : "",
    asButton && isMobile ? "w-full" : "",
  ]
    .filter(Boolean)
    .join(" ");

export default EventTicketPurchasePanel;
